import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMatch } from "react-router-dom";

import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import { useSocket } from "../../lib/socket";
import { useCloseCall } from "../../lib/call-overlay";
import type { User } from "../../types";
import { AppFrame } from "../../components/AppFrame";
import { NewCallDialog } from "../../components/sales/NewCallDialog";
import { CallPage } from "./CallPage";
import { SalesSidebar } from "../../components/sales/SalesSidebar";
import { Button } from "../../components/ui/Button";

/**
 * The sales module's shell: the same frame the other two products render in,
 * with its own navigation and its own toolbar.
 *
 * The socket is the shared one rather than a third connection, and the config
 * is fetched here rather than in each screen -- the groups, tiers and scores
 * are needed by the worklist's filters, the new-call form and the closing form
 * alike, and fetching them once at the shell means no screen paints a call with
 * a blank group while it waits.
 */
export function SalesShell({ user }: { user: User }) {
  const { connected } = useSocket();
  const closeCall = useCloseCall();
  const [raising, setRaising] = useState(false);

  /*
   * The open call, when it was reached without a page to keep behind it -- a
   * link from a ticket or the CRM, a pasted URL, a refresh. It is drawn over
   * the worklist that `/sales/calls/:id` renders.
   *
   * Opened from the list or the board, the URL this sees is that page's, so
   * nothing matches here and `App` draws the call over it instead. See
   * `call-overlay.ts` for why both exist.
   */
  const open = useMatch("/sales/calls/:callId");

  const config = useQuery({
    queryKey: queryKeys.salesConfig,
    queryFn: salesApi.config,
    /* Five minutes: these lists change when a manager edits them, which is
       rare, and every edit invalidates this key directly. */
    staleTime: 5 * 60_000,
  });

  return (
    <>
      {/* Outside the frame so it is never clipped by a pane, and mounted here
          rather than in a page so raising a call from the worklist and from the
          landing view are the same dialog in the same state. */}
      {raising ? (
        <NewCallDialog
          onClose={() => setRaising(false)}
          config={config.data}
          currentUserId={user.id}
          isAdmin={user.role === "admin"}
        />
      ) : null}

      {open ? (
        <CallPage
          user={user}
          onClose={closeCall}
        />
      ) : null}

      <AppFrame
        user={user}
        title="Sales calls"
        connected={connected}
        sidebar={<SalesSidebar connected={connected} />}
        toolbar={
          <Button size="sm" onClick={() => setRaising(true)}>
            New call
          </Button>
        }
      />
    </>
  );
}

/**
 * The call, drawn over the sales page it was opened from.
 *
 * Rendered by `App` beside the routes rather than inside this shell, because
 * the shell underneath is rendering the *background* location and would never
 * match the call's URL. See `call-overlay.ts`.
 */
export function CallOverOrigin({ user }: { user: User }) {
  const closeCall = useCloseCall();
  return <CallPage user={user} onClose={closeCall} />;
}
